import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { syncApi, terminalsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { AdminTerminal, SyncRejectionRow } from '../../api/types.js';
import { Banner } from '../../components/Banner.js';
import { formatDateTime, hoursSince } from '../../lib/format.js';

const QUIET_HOURS = 6;

export function SyncHealthPage() {
  const { t } = useI18n();
  const [terminals, setTerminals] = useState<AdminTerminal[]>([]);
  const [rejections, setRejections] = useState<SyncRejectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [tm, rej] = await Promise.all([terminalsApi.list(), syncApi.rejections()]);
      setTerminals(tm);
      setRejections(rej);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const unresolved = rejections.filter((r) => !r.resolvedAt);

  async function resolve(id: number) {
    try {
      await syncApi.resolveRejection(id);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('sync.title')}</h1>
          <p>{t('sync.subtitle')}</p>
        </div>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <>
          <div className="card">
            <h2>{t('terminals.title')}</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('sync.terminal')}</th>
                    <th>{t('terminals.lastSeen')}</th>
                    <th>{t('terminals.lastSync')}</th>
                    <th>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {terminals
                    .filter((tm) => tm.active)
                    .map((tm) => {
                      const quietHours = hoursSince(tm.lastSyncAt);
                      const isQuiet = quietHours === null || quietHours > QUIET_HOURS;
                      return (
                        <tr key={tm.id}>
                          <td>{tm.name}</td>
                          <td className="numeric">{tm.lastSeenAt ? formatDateTime(tm.lastSeenAt) : t('terminals.never')}</td>
                          <td className="numeric">{tm.lastSyncAt ? formatDateTime(tm.lastSyncAt) : t('terminals.never')}</td>
                          <td>
                            <span className={`badge badge--${isQuiet ? 'warn' : 'ok'}`}>
                              {isQuiet ? t('sync.needsAttention') : t('sync.allClear')}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h2>{t('sync.rejectedTitle')}</h2>
            {unresolved.length === 0 ? (
              <p>{t('sync.rejectedEmpty')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('sync.terminal')}</th>
                      <th>{t('sync.rejectedKind')}</th>
                      <th>{t('sync.rejectedCode')}</th>
                      <th>{t('sync.rejectedMessage')}</th>
                      <th>{t('sync.rejectedAt')}</th>
                      <th>{t('sync.rejectedAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unresolved.map((r) => (
                      <tr key={r.id}>
                        <td>{r.terminalName ?? '—'}</td>
                        <td>{r.kind}</td>
                        <td className="numeric">{r.code}</td>
                        <td>{r.message}</td>
                        <td className="numeric">{formatDateTime(r.receivedAt)}</td>
                        <td>
                          <button type="button" onClick={() => void resolve(r.id)}>
                            {t('sync.resolve')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
